import React from 'react';

/**
 * Premium loading skeleton system with multiple variants.
 * Themed shimmer effect via CSS variables — works with all 8 themes.
 */

/**
 * Base skeleton element — single animated shimmer bar.
 */
export function Skeleton({ className = '', style = {} }) {
  return (
    <div
      className={`skeleton rounded-lg animate-pulse ${className}`}
      style={{
        background: `linear-gradient(90deg,
          var(--bg-card, #161b22) 25%,
          color-mix(in srgb, var(--accent-primary) 8%, var(--bg-card)) 50%,
          var(--bg-card, #161b22) 75%)`,
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.8s infinite ease-in-out',
        ...style,
      }}
    />
  );
}

/**
 * Card skeleton — mimics StatCard layout.
 */
export function CardSkeleton({ count = 4 }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border overflow-hidden"
          style={{
            background: 'var(--bg-card)',
            borderColor: 'var(--border-color)',
            padding: '1.25rem',
          }}
        >
          <Skeleton className="h-3 w-1/2 mb-3" />
          <Skeleton className="h-7 w-3/4 mb-2" />
          <Skeleton className="h-2.5 w-1/3" />
        </div>
      ))}
    </div>
  );
}

/**
 * Table skeleton — multiple rows with shimmer.
 */
export function TableSkeleton({ rows = 5, columns = 4 }) {
  return (
    <div
      className="rounded-2xl border overflow-hidden"
      style={{
        background: 'var(--bg-card)',
        borderColor: 'var(--border-color)',
        padding: '1.25rem',
      }}
    >
      {/* Header row */}
      <div className="flex items-center gap-4 mb-4 pb-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1" />
        ))}
      </div>

      {/* Data rows */}
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            {Array.from({ length: columns }).map((_, j) => (
              <Skeleton key={j} className="h-9 flex-1" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Chart skeleton — large rectangular shimmer area.
 */
export function ChartSkeleton({ height = 'h-64' }) {
  return (
    <div
      className="rounded-2xl border overflow-hidden p-5"
      style={{
        background: 'var(--bg-card)',
        borderColor: 'var(--border-color)',
      }}
    >
      <Skeleton className="h-4 w-1/4 mb-4" />
      <Skeleton className={height} />
    </div>
  );
}

/**
 * Avatar skeleton — circular with optional size.
 */
export function AvatarSkeleton({ size = 10 }) {
  return <Skeleton className={`w-${size} h-${size} rounded-full`} />;
}

/**
 * Text skeleton — single or multiple lines.
 */
export function TextSkeleton({ lines = 1, className = '' }) {
  if (lines === 1) {
    return <Skeleton className={`h-4 ${className}`} />;
  }
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-4 ${i === lines - 1 ? 'w-3/4' : ''}`} />
      ))}
    </div>
  );
}

/**
 * Stat card skeleton — specific layout for dashboard stats.
 */
export function SkeletonStat() {
  return (
    <div
      className="rounded-2xl border p-5"
      style={{
        background: 'var(--bg-card)',
        borderColor: 'var(--border-color)',
      }}
    >
      <Skeleton className="h-3 w-1/2 mb-2" />
      <Skeleton className="h-8 w-3/4 mb-2" />
      <Skeleton className="h-2.5 w-1/3" />
    </div>
  );
}

/**
 * List skeleton — item list with avatar + text.
 */
export function ListSkeleton({ items = 5 }) {
  return (
    <div
      className="rounded-2xl border overflow-hidden p-4"
      style={{
        background: 'var(--bg-card)',
        borderColor: 'var(--border-color)',
      }}
    >
      <div className="space-y-4">
        {Array.from({ length: items }).map((_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Page skeleton — full page layout with header + content grid.
 */
export function PageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStat key={i} />
        ))}
      </div>

      {/* Chart section */}
      <ChartSkeleton />

      {/* Table section */}
      <TableSkeleton rows={5} />
    </div>
  );
}

// Default export for backwards compatibility
export default function LoadingSkeleton({ variant = 'card', count = 4, rows = 5, height = 'h-64' }) {
  switch (variant) {
    case 'card':
      return <CardSkeleton count={count} />;
    case 'table':
      return <TableSkeleton rows={rows} />;
    case 'chart':
      return <ChartSkeleton height={height} />;
    case 'stat':
      return <SkeletonStat />;
    case 'list':
      return <ListSkeleton items={count} />;
    case 'page':
      return <PageSkeleton />;
    default:
      return <CardSkeleton count={count} />;
  }
}
